import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, UserCheck, FlaskConical, Fingerprint, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Framework } from "@/components/Framework";
import { FadeIn } from "@/components/Reveal";
import { TSM4_PRODUCTS } from "@/config/site";

const ICONS = { shield: ShieldCheck, "user-check": UserCheck, flask: FlaskConical, fingerprint: Fingerprint };

export default function Tsm4Page() {
  useEffect(() => {
    document.title = "TSM4 Solutions | Four Solutions. One Smarter Business. | TSM Group CLE";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "TSM4 by TSM Group CLE: MITIVISE risk & compliance platform, background screening, drug testing, and fingerprinting — four solutions for a smarter, safer business.");
  }, []);

  return (
    <div className="bg-paper" data-testid="tsm4-page">
      <PageHero
        testid="tsm4-hero"
        eyebrow="The TSM4 Product Suite"
        title={<>Four Solutions. <span className="text-gold">One Smarter Business.</span></>}
        copy="TSM4 brings TSM Group CLE's technology and services into one connected family: a digital risk and compliance platform, workforce screening, and business readiness services that grow with you."
      />

      <section className="py-24 lg:py-32">
        <div className="container-tsm">
          <div className="grid gap-8 lg:grid-cols-2">
            {TSM4_PRODUCTS.map((p, i) => {
              const Icon = ICONS[p.icon];
              return (
                <FadeIn key={p.name} delay={i * 0.08} className="h-full">
                  <article
                    className={`flex h-full flex-col rounded-xl border border-navy/10 border-l-8 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgb(23,43,58,0.12)] ${p.status === "coming_soon" ? "border-l-teal/40" : "border-l-teal"}`}
                    data-testid={`tsm4-overview-card-${i + 1}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${p.status === "coming_soon" ? "bg-navy" : "bg-teal"} text-white`}>
                        <Icon className="h-6 w-6" />
                      </span>
                      {p.status === "coming_soon" && (
                        <span className="inline-flex items-center rounded-full border border-dashed border-tealdeep/60 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-tealdeep">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <h2 className="mt-6 text-2xl font-extrabold tracking-tight text-navy">{p.name}</h2>
                    <p className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-tealdeep">{p.label}</p>
                    <p className="mt-4 flex-1 leading-relaxed text-ink/70">{p.description}</p>
                    <div className="mt-7">
                      <Link to={p.href} data-testid={`tsm4-overview-cta-${i + 1}`} className="btn-gold !px-6 !py-3 text-xs">
                        {p.status === "coming_soon" ? "Join the Interest List" : p.name === "MITIVISE" ? "Explore MITIVISE" : `Explore ${p.name.replace("TSM ", "")}`}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </article>
                </FadeIn>
              );
            })}
          </div>

          <FadeIn delay={0.15}>
            <div className="mt-16 flex flex-col items-center gap-6 rounded-xl border border-navy/10 bg-white p-9 text-center" data-testid="tsm4-engagement">
              <CheckCircle2 className="h-8 w-8 text-teal" />
              <h3 className="max-w-2xl text-2xl font-extrabold tracking-tight text-navy">
                Not sure where to start? The free Business Readiness Assessment shows you in eight questions.
              </h3>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Link to="/mitivise/assessment" data-testid="tsm4-assessment-cta" className="btn-gold">
                  Take the Free Assessment
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" data-testid="tsm4-contact-cta" className="btn-outline">Talk With TSM</Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <Framework />
    </div>
  );
}
